import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
} from '@mui/material'
import { useCustomViewNoteHistory } from '@/hooks/useCustomViewNoteHistory'
import type { ContentType } from '@/types/api'

interface NoteHistoryDialogProps {
  open: boolean
  viewId: number | null
  contentType: ContentType
  entityId: number
  onClose: () => void
  onRestore: (body: string) => void
}

/**
 * Read-only window onto the note's system-versioned history. Restoring does not write from here:
 * it loads the chosen text into the editor, which then saves through the normal token-checked
 * path, so a restore cannot clobber a concurrent edit any more than typing could.
 */
export function NoteHistoryDialog({
  open, viewId, contentType, entityId, onClose, onRestore,
}: NoteHistoryDialogProps) {
  const { t, i18n } = useTranslation()
  const { data, isLoading, isError } = useCustomViewNoteHistory(viewId, contentType, entityId, open)
  const [selected, setSelected] = useState<number | null>(null)

  const versions = data?.versions ?? []
  const chosen = selected === null ? null : versions[selected] ?? null
  const formatter = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('notes.historyTitle')}</DialogTitle>
      <DialogContent>
        {isLoading && <CircularProgress size={24} />}
        {isError && <Alert severity="error">{t('notes.historyFailed')}</Alert>}
        {!isLoading && !isError && data?.supported === false && (
          <Alert severity="info">{t('notes.historyUnsupported')}</Alert>
        )}
        {!isLoading && !isError && data?.supported && versions.length === 0 && (
          <Alert severity="info">{t('notes.historyEmpty')}</Alert>
        )}
        {versions.length > 0 && (
          <div className="note-history">
            <ul className="note-history-list">
              {versions.map((v, i) => (
                <li key={`${v.validFrom}-${i}`}>
                  <button
                    type="button"
                    className={`note-history-item${i === selected ? ' note-history-item--selected' : ''}`}
                    onClick={() => setSelected(i)}
                  >
                    <span className="note-history-when">{formatter.format(new Date(v.validFrom))}</span>
                    <span className="note-history-who">{v.updatedBy ?? t('notes.conflictSomeone')}</span>
                  </button>
                </li>
              ))}
            </ul>
            <pre className="note-history-preview">{chosen?.body ?? t('notes.historyPickOne')}</pre>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('notes.conflictCancel')}</Button>
        <Button
          variant="contained"
          disabled={chosen === null}
          onClick={() => { if (chosen) { onRestore(chosen.body); onClose() } }}
        >
          {t('notes.historyRestore')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
